import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Icon, IconButton, Text, VStack} from '@chakra-ui/react';
import {useApi} from "@/uses/useApi";
import useScreenState from "@/uses/useScreenState";
import {
    ResourceCategoryBody,
    ResourceCategoryBodySkeleton,
    CreateOrEditResourceCategory,
    EntityListBody,
    EntityListBodySkeleton,
    EntityListHeader,
    EntityListHeaderSkeleton,
    EntityModalContent,
    ListHeader,
    Paginator,
    PaginatorSkeleton
} from "@/components";
import {useModal} from "@/contexts/modalContext";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useLanguage} from "@/contexts/languageContext";
import objectUtil from "@/utils/objectUtil";
import {Plus} from 'lucide-react';

const PER_PAGE = 20;

export const ResourceCategoryList = () => {

    const navigate = useNavigate();
    const {language, t} = useLanguage();
    const sizes = {base: '95%', md: '95%', lg: '90%'};
    const api = useApi();
    const {ApiService} = api;
    const [entities, setEntities] = useState([]);

    const {open, close} = useModal();
    const [itemNumber, setItemNumber] = useState(null);
    const [filters, setFilters] = useState({});

    const [currentQueryParameters, setSearchParams] = useSearchParams();
    const getDefaultState = useCallback((pageParams) => {
        const result = {}

        /** Retrieve filters param */
        const filter = pageParams.get('filter');
        try {
            result.filter = JSON.parse(filter);
            if (objectUtil.isObject(result.filter)) {
                result.filter = Object.fromEntries(Object.entries(result.filter).filter(([_, value]) => (value ?? []).length > 0));
            }
        } catch (e) {
            console.error(e);
            result.filter = {};
        }

        /** Retrieve search param */
        result.search = pageParams.get('search') ?? '';

        /** Retrieve page param */
        const parsedPage = parseInt(pageParams.get('page'), 10);
        if (isNaN(parsedPage) || parsedPage <= 0) {
            result.page = 0;
        } else {
            result.page = parsedPage - 1;
        }
        return result;
    }, []);
    const defaultParams = getDefaultState(currentQueryParameters);
    const [getParams, setGetParams] = useState({
        filter: {entityType: 'resource', ...(defaultParams.filter ?? {})},
        ...(defaultParams.page > 0 ? {page: defaultParams.page} : {}),
        ...(defaultParams.search && defaultParams.search.length >= process.env.REACT_APP_MIN_SEARCH_LENGTH ? {search: defaultParams.search} : {}),
    });

    const setResourceCategoryGetParams = useCallback((value) => {
        setGetParams((prevState) => {
            const nextState = typeof value === 'function' ? value(prevState) : value;

            return {
                ...nextState,
                filter: {entityType: 'resource', ...(nextState.filter ?? {})},
            }
        })
    }, []);

    const {state, realState, isMounted, setState} = useScreenState();


    const reqParams = useRef(null);
    const getPage = useCallback(() => {
        /** If page is different, it's only a pagination action,
         * so no need to show skeleton for paginator
         */
        const newState = reqParams.current !== null &&
        objectUtil
            .difference(getParams, reqParams.current ?? {})
            .filter((e) => !['page'].includes(e))
            .length !== 0
            ? 1 : 0;
        setState((prevState) => prevState === -1 ? prevState : newState);
        reqParams.current = getParams
        return ApiService.get('category', {
            ...reqParams.current,
            limit: PER_PAGE,
            locale: language,
            ...(state === -1 ? {needFilters: 'bool:true'} : {})
        })
            .then(res => {
                if (res?.data?.type === 'success') {
                    setItemNumber(() => res?.data?.value?.estimatedTotalHits);
                    setEntities(() => (res?.data?.value?.hits ?? []));
                }
                if (state !== -1) {
                    return null;
                }

                return ApiService.get('category', {
                    filter: {entityType: 'resource', root: true},
                    limit: 1000,
                    locale: language,
                })
                    .then(res => {
                        if (res?.data?.type === 'success') {
                            const items = (res?.data?.value?.hits ?? []).map((category) => ({
                                label: category?.[`${language}Label`],
                                value: category.id,
                            }));

                            setFilters(items.length > 0 ? {
                                parent: {
                                    placeholder: `${t('sentences.actionField', {action: t('actions.filterBy'), element: t('objects.category_one')})}`,
                                    items,
                                }
                            } : {});
                        }
                    });
            })
            .catch(e => console.log(e?.response?.data))
            .finally(() => setState(_ => 2))
    }, [ApiService, getParams, language, setState, state, t]);

    useEffect(() => {
        getPage().then(() => {
            /** Manage persistent state of search query */
            setSearchParams(prev => {
                if ('search' in getParams) {
                    prev.set('search', getParams.search);
                } else {
                    prev.delete('search');
                }

                const visibleFilters = objectUtil.filterByKeys(getParams.filter ?? {}, ['entityType'], false);
                if (Object.keys(visibleFilters).length > 0) {
                    prev.set('filter', JSON.stringify(visibleFilters));
                } else {
                    prev.delete('filter');
                }

                if ('page' in getParams) {
                    prev.set('page', (getParams.page + 1).toString());
                } else {
                    prev.delete('page');
                }

                return prev;
            })
        })
    }, [getParams]);

    const hasData = useMemo(() => {
        return isMounted && (entities.length > 0 || Object.keys(getParams).length > 0)
    }, [isMounted, entities, getParams]);

    const noDataText = t('sentences.noEntityYet.category');

    const Table = useMemo(() => {
        return hasData
            ?
            <VStack position="relative" w="100%" overflow="hidden" flex={1} pb="2vh">
                <EntityListHeader
                    after={<IconButton
                        bg={"secondary.500"}
                        onClick={(_) => {
                            open(<EntityModalContent
                                title={t('actions.add')}
                                Body={<CreateOrEditResourceCategory
                                    successCallback={() => {
                                        navigate(0);
                                    }}
                                />}
                                close={close}
                            />, 'cover')
                        }}
                    >
                        <Icon as={Plus}/>
                    </IconButton>}
                    setGetParams={setResourceCategoryGetParams}
                    filters={filters}
                    defaultParams={defaultParams}
                    sizes={sizes}
                />
                <VStack
                    flex={1}
                    w={sizes}
                    py="1rem"
                    align="center"
                    justify="flex-start"
                    overflow="auto"
                    color="secondary.500"
                >
                    {
                        [0, 1].includes(realState)
                            ? <EntityListBodySkeleton
                                Body={ResourceCategoryBodySkeleton}
                                sizes={sizes}/>
                            : <EntityListBody
                                noDataText={noDataText}
                                entity="category"
                                Body={ResourceCategoryBody}
                                EditBody={CreateOrEditResourceCategory}
                                sizes={sizes}
                                entities={entities}
                                setEntities={setEntities}
                                getDeletionText={(e) => `"${e?.[`${language}Label`]}"`.slice(0,200)}
                            />
                    }
                </VStack>
                {
                    realState === 1
                        ? <PaginatorSkeleton/>
                        : <Paginator
                            defaultPage={defaultParams.page}
                            setGetParams={setGetParams}
                            itemNumber={itemNumber}
                            perPage={PER_PAGE}
                        />
                }
            </VStack>
            :
            <VStack align="center" justify="center">
                <Text>{noDataText}</Text>
                <IconButton
                    bg={'secondary.500'}
                    onClick={(_) => {
                        open(<EntityModalContent
                            title={t('actions.add')}
                            Body={<CreateOrEditResourceCategory
                                successCallback={() => {
                                    navigate(0);
                                }}
                            />}
                            close={close}
                        />, 'cover')
                    }}
                >
                    <Icon as={Plus}/>
                </IconButton>
            </VStack>
    }, [hasData, realState, entities, setEntities, setResourceCategoryGetParams, filters, defaultParams, sizes, itemNumber, close, navigate])

    const Content = useMemo(() => {
        return isMounted
            ? Table
            : <VStack w="100%" overflow="hidden" flex={1} pb="2vh">
                <EntityListHeaderSkeleton
                    canSort={false}
                    hasFilters={false}
                    sizes={sizes}
                    after={<IconButton/>}
                />
                <VStack
                    flex={1}
                    w={sizes}
                    py="1rem"
                    align="center"
                    justify="flex-start"
                    overflow="auto"
                    color="secondary.500"
                >
                    <EntityListBodySkeleton
                        Body={ResourceCategoryBodySkeleton}
                        sizes={sizes}/>
                </VStack>
                <PaginatorSkeleton/>
            </VStack>
    }, [isMounted, Table])

    return (
        <VStack w="100%" overflow="hidden">
            <ListHeader title={t('sentences.managementOfEntities', { entity: t('objects.category_other') })} description={t('sentences.manageEntities', { action1: t('actions.add'), action2: t('actions.edit'), action3: t('actions.view'), entity: t('objects.category_other')})}/>
            {
                /** Wait for options to be retrieved otherwise, default state isn't took in count */
                Content
            }
        </VStack>
    );
};

export default ResourceCategoryList;
